import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormArray, FormControl, FormGroup } from "@angular/forms";
import { Yii } from "../../../../../libraries/interfaces/Yii";
import { IFieldSpecs } from "libraries/module/formbuilder/fields/interfaces/field-specs";
import {
  ICustomerAgency,
  IWatabiLightBooking
} from "libraries/interfaces/watabilight/IWatabiLightBooking.interface";
import { GenericComponent, IAddressValue } from "../generic-component/generic-component.directive";
import { WatabilightService } from "../../../../service/watabilight.service";
import { MatDialog } from "@angular/material/dialog";
import { ModalLinkComponent } from "../modal-link/modal-link.component";

class AgencyWording {
  public readonly mainTitle: string = Yii.t('wui-shared-js', "Agence");
  public readonly loader: string = Yii.t('wui-shared-js', "téléchargement du logo");
  public readonly tooLagre: string = Yii.t('wui-shared-js', "Fichier trop volumineux");
  public readonly noAgency: string = Yii.t('wui-shared-js', "Agence non définie");
}

interface IAgencyConfig {
  id: IFieldSpecs;
  nameConfig: IFieldSpecs;
  logoConfig: IFieldSpecs;
  mailConfig: IFieldSpecs;
  phoneConfig: IFieldSpecs;
  address: {
    zipCodeConfig: IFieldSpecs;
    nameStreetConfig: IFieldSpecs[];
    cityConfig: IFieldSpecs;
    countryConfig: IFieldSpecs;
  };
}

interface IAgencyValue {
  id: number;
  name: string;
  logo: string;
  mail: string;
  phone: string;
  address: IAddressValue;
}

enum EFormControlName {
  id = "id",
  name = "name",
  logo = "logo",
  mail = "mail",
  phone = "phone",
  address = "address",
  zipCode = "zipCode",
  nameStreet = "nameStreet",
  city = "city",
  country = "country"
}

@Component({
  selector: 'watabilight-agency',
  templateUrl: './agency.component.html',
  styleUrls: [
      './agency.component.scss',
      '../../booking.component.scss'
  ]
})
export class AgencyComponent extends GenericComponent implements OnInit {
  @Input()
  public booking: IWatabiLightBooking;
  @Input()
  public isEditable: boolean;

  @Output()
  public onEdit: EventEmitter<number> = new EventEmitter();

  public agencyWording: AgencyWording = new AgencyWording();
  public isEdit: boolean = false;

  public config: IAgencyConfig;
  public formValue: IAgencyValue;
  public address: string = "";

  public iconMail: string = "fa-at";
  public iconPhone: string = "fa-phone";
  public iconLogo: string = "fa-image";
  public iconAddress: string = "fa-home";

  public isInternalError: boolean = true;

  public loader: boolean = false;
  public loaderMsg: string = this.agencyWording.loader;

  public maxFileSize: number = 2097152;
  public displayAgency: boolean = true;

  public editableFields: string[];

  constructor(
      private watabilightService: WatabilightService,
      private readonly cdf: ChangeDetectorRef,
      private readonly dialog: MatDialog,
  ) {
    super();
  }

  public ngOnInit(): void {
    this.title = this.sectionConfig?.title ? this.sectionConfig?.title : this.agencyWording.mainTitle;
    this.description = this.sectionConfig?.description ? this.sectionConfig?.description : "";
    this.initFormControl();
    this.initFormFieldConfig();
    this.setCustomerValue();
    this.patchValueFromForm();
    this.editableFields = this.sectionConfig.editableFields;

    this.onEdit.emit(4);

    if (!this.booking?.customerAgency?.id) {
      this.displayAgency = false;
      this.handleError(this.agencyWording.noAgency);
    }
  }

  public update(): void {
    this.cdf.markForCheck();
  }

  public edit(): void {
    if (!this.isEdit) {
      this.isEdit = true;
      this.onEdit.emit(5);
    }
    else {
      this.form.markAllAsTouched();
      if (this.form.valid && this.checkEmail(this.formValue.mail)) {
        this.onEdit.emit(4);
        this.isEdit = false;

        this.handleError();
      }
    }
  }

  public initFormControl(): void {
    this.form.setControl(EFormControlName.id, new FormControl());
    this.form.setControl(EFormControlName.name, new FormControl());
    this.form.setControl(EFormControlName.logo, new FormControl());
    this.form.setControl(EFormControlName.mail, new FormControl());
    this.form.setControl(EFormControlName.phone, new FormControl());
    this.form.setControl(EFormControlName.address, new FormGroup({
      [EFormControlName.zipCode]: new FormControl(),
      [EFormControlName.nameStreet]: new FormArray([]),
      [EFormControlName.city]: new FormControl(),
      [EFormControlName.country]: new FormControl(),
    }));
  }

  public initFormFieldConfig(): void {
    const addressFormGroup: FormGroup = this.form.get("address") as FormGroup;
    this.config = {
      id: this.getFieldConfig(this.form, EFormControlName.id),
      nameConfig: this.getFieldConfig(this.form, EFormControlName.name),
      logoConfig: this.getFieldConfig(this.form, EFormControlName.logo),
      mailConfig: this.getFieldConfig(this.form, EFormControlName.mail, true),
      phoneConfig: this.getFieldConfig(this.form, EFormControlName.phone),
      address: {
        zipCodeConfig: this.getFieldConfig(addressFormGroup, EFormControlName.zipCode),
        nameStreetConfig: [],
        cityConfig: this.getFieldConfig(addressFormGroup, EFormControlName.city),
        countryConfig: this.getFieldConfig(addressFormGroup, EFormControlName.country)
      }
    };
  }

  public setCustomerValue(): void {
    this.patchValueIfExist(this.getAgValStr(["id"]), EFormControlName.id);
    this.patchValueIfExist(this.getAgValStr(["name", "value"]), EFormControlName.name);
    this.patchValueIfExist(this.booking?.customerAgency?.distributorAgency?.logo?.url, EFormControlName.logo);
    this.patchValueIfExist(this.getFirstOrNull(this.getAgValStrArr(["emailArray"]), "text"), EFormControlName.mail);
    this.patchValueIfExist(this.getFirstOrNull(this.getAgValStrArr(["telephoneArray"]), "text"), EFormControlName.phone);
    this.patchValueIfExist(this.getAgValStr(["address", "zipCode"]), ["address", EFormControlName.zipCode]);
    this.patchValueIfExist(this.getFirstOrNull(this.getAgValStrArr(["address", "city", "nameArray"]), "value"), ["address", EFormControlName.city]);
    this.patchValueIfExist(this.getFirstOrNull(this.getAgValStrArr(["address", "country", "nameArray"]), "value"), ["address", EFormControlName.country]);
    this.initAdressForm(this.getAgValStrArr(["address", "streetLineArray"]), EFormControlName.nameStreet, this.config);
  }

  public addAddLine(id: number) {
    this.addComplement(this.booking?.customerAgency?.address?.streetLineArray, EFormControlName.nameStreet, this.config, id);
  }

  public patchValueFromForm(): void {
    this.formValue = this.form.value;
    this.checkEmail(this.formValue.mail);
    this.address = this.setAddress(this.formValue.address);
  }

  public editAgencyEvent(): void {
    this.patchValueFromForm();
    this.checkEmail(this.formValue.mail);
    this.onEdit.emit(4);
  }

  public open(): void {
    const dialog = this.dialog.open(ModalLinkComponent, {
      width: 'fit-content',
      height: 'fit-content',
      data: {url: this.formValue.logo, type: "img"}
    });
  }

  public upladImage(event: any): void {
    this.loader = true;
    const logo: File = event.target.files[0];

    if (logo) {
      if (logo.size > this.maxFileSize) {
        this.handleError(this.agencyWording.tooLagre)
        this.loader = false;
      }
      else {
        const form_data: FormData = new FormData();
        form_data.append('file', logo, logo.name);
        const ext: string = logo.name.split(".").pop();
        const idAgence: any = this.getAgValStr(["id"]);
        this.watabilightService.uploadFile(form_data, idAgence ? idAgence : 1, ext ? ext: ".png").subscribe({
          next: (value: string) => {
            this.patchValueIfExist(value, EFormControlName.logo);
            this.formValue.logo = value;
            this.handleError();
            this.loader = false;
          },
          error: (err: any) => {
            this.handleError(err.message);
            this.loader = false;
          }
        });
      }
    }
  }

  private find(data: any, key: string[]): any | any[] {
    const editKey: string[] = Object.assign([], key);
    if (editKey.length === 1 && data[editKey[0]]) {
        return (data[editKey[0]]);
    }
    else if (editKey.length > 1 && data[editKey[0]]) {
      let index: string = editKey[0];
      editKey.shift();
      return this.find(data[index], editKey);
    }
    return null;
  }

  private getAgValStr(key: string[]): string | number  {
    const agency: ICustomerAgency = this.booking.customerAgency;
    return this.find(agency?.distributorAgency?.customerAgency, key) ? this.find(agency?.distributorAgency?.customerAgency, key) : this.find(agency, key);
  }

  private getAgValStrArr(key: string[]): any[] {
    const agency: ICustomerAgency = this.booking.customerAgency;
    return this.find(agency?.distributorAgency?.customerAgency, key) ? this.find(agency?.distributorAgency?.customerAgency, key) : this.find(agency, key);
  }

}
